import React, {useState} from 'react';
import {Box, Button, Checkbox, IconButton, Radio, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {MultipleChoiceQuestion, Option} from 'crqlar-react-common/dist/models/forms/form';
import EditableTypography from 'crqlar-react-common/dist/components/EditableTypography';
import MdEditor from 'crqlar-react-common/dist/components/MdEditor';
import {useTranslation} from 'react-i18next';

interface MultipleChoiceEditorProps {
    question: MultipleChoiceQuestion;
    onQuestionUpdate: (question: MultipleChoiceQuestion) => void;
}

const MultipleChoiceEditor: React.FC<MultipleChoiceEditorProps> = ({
                                                                       question,
                                                                       onQuestionUpdate
                                                                   }) => {
    const {t} = useTranslation();
    // Generate a unique ID for new options
    const generateUniqueId = () => Math.random().toString(36).substring(2, 15);

    // Handle title update
    const handleTitleUpdate = (value: string) => {
        onQuestionUpdate({
            ...question,
            title: value,
        });
    };

    // Handle description update
    const handleDescriptionUpdate = (description?: string) => {
        onQuestionUpdate({
            ...question,
            description: description || ''
        });
    };

    // Handle option text update
    const handleOptionUpdate = (optionId: string, text: string) => {
        const updatedOptions = question.options?.map(opt =>
            opt.id === optionId ? {...opt, label: text} : opt
        );

        onQuestionUpdate({
            ...question,
            options: updatedOptions,
        });
    };

    // Handle add new option
    const handleAddOption = () => {
        const newOption: Option = {
            id: generateUniqueId(),
            label: t('Option {{number}}', {number: (question.options?.length || 0) + 1}),
        };

        onQuestionUpdate({
            ...question,
            options: [...(question.options || []), newOption],
        });
    };

    // Handle delete option
    const handleDeleteOption = (optionId: string) => {
        onQuestionUpdate({
            ...question,
            options: question.options?.filter(opt => opt.id !== optionId),
        });
    };

    // Toggle multiple selection mode
    const handleToggleMultiple = () => {
        onQuestionUpdate({
            ...question,
            allowMultiple: !question.allowMultiple,
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: 600,
                p: 2,
            }}
        >
            {/* Question Title */}
            <EditableTypography
                value={question.title}
                onChange={handleTitleUpdate}
                placeholder={t('Your question here')}
                variant="h6"
                multiline
                sx={{
                    mb: 2,
                    width: '100%',
                    fontWeight: 'medium',
                    textAlign: 'left',
                    padding: '8px',
                    borderRadius: 1,
                }}
            />

            {/* Description */}
            <Box sx={{mb: 3, width: '100%'}}>
                <MdEditor
                    value={question.description || ''}
                    onChange={handleDescriptionUpdate}
                    placeholder={t('Question description (optional)')}
                    className={'mini-mdx'}
                />
            </Box>

            {/* Multiple Selection Toggle */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                        opacity: 0.8,
                    }
                }}
                onClick={handleToggleMultiple}
            >
                {question.allowMultiple ? (
                    <CheckBoxIcon sx={{mr: 1, fontSize: 20}} />
                ) : (
                    <CheckBoxOutlineBlankIcon sx={{mr: 1, fontSize: 20}} />
                )}
                <Typography variant="body2">{t('Allow multiple selections')}</Typography>
            </Box>

            {/* Options List - Scrollable */}
            <Box
                sx={{
                    width: '100%',
                    maxHeight: 400,
                    overflowY: 'auto',
                    mb: 2,
                    pr: 1,
                }}
            >
                {question.options?.map((option, index) => (
                    <Box
                        key={option.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1.5,
                        }}
                    >
                        {/* Radio or Checkbox icon */}
                        {question.allowMultiple ? (
                            <Checkbox
                                disabled
                                size="small"
                            />
                        ) : (
                            <Radio
                                disabled
                                size="small"
                            />
                        )}

                        {/* Editable option text */}
                        <Box sx={{flex: 1, ml: 1}}>
                            <EditableTypography
                                value={option.label}
                                onChange={v => handleOptionUpdate(option.id, v)}
                                sx={{
                                    textAlign: 'left',
                                    padding: '4px 8px',
                                    borderRadius: 1,
                                    width: '100%',
                                    color: 'text.secondary',
                                }}
                                placeholder={t('Option {{number}}', {number: index + 1})}
                            />
                        </Box>

                        {/* Delete button */}
                        <IconButton
                            size="small"
                            onClick={() => handleDeleteOption(option.id)}
                            sx={{
                                ml: 1,
                                '&:hover': {
                                    color: 'error.main',
                                }
                            }}
                        >
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    </Box>
                ))}
            </Box>

            {/* Add Option Button */}
            <Button
                startIcon={<AddIcon/>}
                onClick={handleAddOption}
                variant="outlined"
                sx={{
                    borderStyle: 'dashed',
                    textTransform: 'none',
                    borderRadius: 1,
                    py: 1,
                    width: '100%',
                    justifyContent: 'flex-start',
                }}
            >
                {t('Add choice')}
            </Button>
        </Box>
    );
};

export default MultipleChoiceEditor;
